import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type SupportRequest, type SupportTopic } from '../api';

/**
 * Every request this patient has filed, and what came back.
 *
 * `GET /support/requests` and its reply endpoint have existed all along with
 * nothing on the web calling either, so a patient who raised a records
 * question, a billing query or a data-protection request could never see
 * whether anyone had answered. This account has a reply from NeoHealthCard
 * Care sitting unread since 15 September.
 *
 * Data-protection rights are filed here too, under Privacy: there is no
 * separate rights endpoint, and a request recorded this way is one the Data
 * Protection Officer actions with an auditable trail.
 */

const TOPICS: { value: SupportTopic; label: string; hint: string }[] = [
  { value: 'RECORDS', label: 'Records', hint: 'A report, prescription or document that is missing or wrong' },
  { value: 'BOOKING', label: 'Booking', hint: 'An appointment, lab test or ambulance booking' },
  { value: 'PAYMENT', label: 'Payment', hint: 'A bill, refund or payment that does not look right' },
  { value: 'PRIVACY', label: 'Privacy & data rights', hint: 'A copy of your data, a correction, or closing your account' },
  { value: 'ACCOUNT_RECOVERY', label: 'Account access', hint: 'Trouble signing in or recovering your account' },
  { value: 'OTHER', label: 'Something else', hint: '' },
];

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 16, boxShadow: T.shadow, fontFamily: T.font,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`,
  borderRadius: 10, fontSize: 13, fontFamily: T.font, color: T.text,
  outline: 'none', boxSizing: 'border-box',
};

function statusTone(status: string): string {
  const s = (status || '').toUpperCase();
  if (s.includes('RESOLV') || s.includes('CLOSED')) return T.success;
  if (s.includes('PROGRESS')) return T.info;
  return T.warning;
}

function pretty(s: string): string {
  return (s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function fmtWhen(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function MyRequests() {
  const [rows, setRows] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [busy, setBusy] = useState('');

  const [topic, setTopic] = useState<SupportTopic>('RECORDS');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await patientApi.supportRequests.list() ?? []);
    } catch (e: any) {
      setError(e?.body?.message || 'Your requests could not be loaded — check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const file = async () => {
    setError(''); setNotice('');
    if (subject.trim().length < 3) { setError('Add a short subject.'); return; }
    if (message.trim().length < 10) { setError('Tell us what happened — at least a sentence.'); return; }
    setBusy('new');
    try {
      await patientApi.supportRequests.create({ topic, subject: subject.trim(), message: message.trim() });
      setSubject(''); setMessage(''); setOpenForm(false);
      setNotice('Your request has been filed. Replies appear here.');
      await load();
    } catch (e: any) {
      setError(e?.body?.errors?.[0]?.message || e?.body?.message || 'The request could not be filed. Nothing was sent.');
    } finally {
      setBusy('');
    }
  };

  const sendReply = async (r: SupportRequest) => {
    if (replyText.trim().length === 0) return;
    setBusy(r.id); setError('');
    try {
      const updated = await patientApi.supportRequests.reply(r.id, replyText);
      setRows((rs) => rs.map((x) => (x.id === r.id ? updated : x)));
      setReplyText(''); setReplyTo(null);
    } catch (e: any) {
      setError(e?.body?.message || 'Your reply could not be sent.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <h1 style={{ ...GOV_TYPE.pageTitle }}>My requests</h1>
          <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
            Questions and data-rights requests you have raised, and the answers.
          </p>
        </div>
        <button
          onClick={() => { setOpenForm((v) => !v); setError(''); setNotice(''); }}
          style={{
            padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 700, color: T.white, background: T.teal,
            fontFamily: T.font, whiteSpace: 'nowrap',
          }}
        >
          {openForm ? 'Close' : 'New request'}
        </button>
      </div>

      {error && <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>{error}</div>}
      {notice && <div style={{ ...card, borderColor: `${T.teal}55`, padding: 12, fontSize: 12, color: T.teal }}>{notice}</div>}

      {openForm && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 12 }}>File a request</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {TOPICS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTopic(t.value)}
                style={{
                  padding: '7px 12px', borderRadius: 20, cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, fontFamily: T.font,
                  color: topic === t.value ? T.white : T.textSec,
                  background: topic === t.value ? T.teal : T.white,
                  border: `1px solid ${topic === t.value ? T.teal : T.border}`,
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {TOPICS.find((t) => t.value === topic)?.hint && (
            <div style={{ fontSize: 11, color: T.textSec, marginBottom: 12, lineHeight: 1.5 }}>
              {TOPICS.find((t) => t.value === topic)?.hint}
            </div>
          )}

          <label htmlFor="req-subject" style={{ fontSize: 11, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 6 }}>
            Subject
          </label>
          <input
            id="req-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="In a few words"
            style={inputStyle}
          />

          <label htmlFor="req-message" style={{ fontSize: 11, fontWeight: 600, color: T.textSec, display: 'block', margin: '12px 0 6px' }}>
            What happened
          </label>
          <textarea
            id="req-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="Give us enough to look it up — dates, report names, order numbers."
            style={{ ...inputStyle, resize: 'vertical' }}
          />

          <button
            onClick={file}
            disabled={busy === 'new'}
            style={{
              marginTop: 14, padding: '11px 20px', borderRadius: 10, border: 'none',
              cursor: busy === 'new' ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700,
              color: T.white, background: T.teal, fontFamily: T.font, opacity: busy === 'new' ? 0.6 : 1,
            }}
          >
            {busy === 'new' ? 'Filing…' : 'File request'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading your requests…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📬</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>No requests yet</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            Anything you raise — a missing report, a billing question, a copy of your data — appears here with its answer.
          </div>
        </div>
      ) : (
        rows.map((r) => (
          <div key={r.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{r.subject}</div>
                <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 2 }}>
                  {r.ticketNumber} · {pretty(r.topic)} · {fmtWhen(r.createdAt)}
                </div>
              </div>
              <span style={{
                fontSize: 9.5, fontWeight: 700, color: T.white, whiteSpace: 'nowrap',
                background: statusTone(r.status), borderRadius: 8, padding: '3px 8px',
              }}>
                {pretty(r.status)}
              </span>
            </div>

            <div style={{ fontSize: 12, color: T.text, marginTop: 10, lineHeight: 1.55 }}>{r.message}</div>

            {r.replies?.length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {r.replies.map((rep) => {
                  const mine = (rep.from || '').toUpperCase() === 'PATIENT';
                  return (
                    <div
                      key={`${rep.at}-${rep.message.slice(0, 12)}`}
                      style={{
                        background: mine ? `${T.teal}0D` : T.ice,
                        border: `1px solid ${mine ? `${T.teal}25` : T.border}`,
                        borderRadius: 12, padding: '10px 12px',
                      }}
                    >
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: mine ? T.teal : T.textSec }}>
                        {mine ? 'You' : rep.authorName || 'NeoHealthCard Care'}
                        <span style={{ fontWeight: 500, color: T.textTer }}> · {fmtWhen(rep.at)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: T.text, marginTop: 4, lineHeight: 1.55 }}>{rep.message}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {replyTo === r.id ? (
              <div style={{ marginTop: 12 }}>
                <textarea
                  aria-label={`Reply to ${r.ticketNumber}`}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={3}
                  placeholder="Add to this request…"
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => sendReply(r)}
                    disabled={busy === r.id || replyText.trim().length === 0}
                    style={{
                      padding: '9px 16px', borderRadius: 10, border: 'none',
                      cursor: 'pointer', fontSize: 12, fontWeight: 700, color: T.white,
                      background: T.teal, fontFamily: T.font,
                      opacity: busy === r.id || replyText.trim().length === 0 ? 0.55 : 1,
                    }}
                  >
                    {busy === r.id ? 'Sending…' : 'Send'}
                  </button>
                  <button
                    onClick={() => { setReplyTo(null); setReplyText(''); }}
                    style={{
                      padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border}`,
                      background: T.white, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      color: T.textSec, fontFamily: T.font,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setReplyTo(r.id); setReplyText(''); }}
                style={{
                  marginTop: 12, padding: '8px 14px', borderRadius: 10,
                  border: `1px solid ${T.border}`, background: T.white, cursor: 'pointer',
                  fontSize: 11.5, fontWeight: 600, color: T.textSec, fontFamily: T.font,
                }}
              >
                Add to this request
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}
